import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import messages from "messages";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import getAllGCals from "services/gcal/getAllGCals";
// import getSelectedGCals from "services/gcal/getSelectedGCals";
import postGCalsToSync from "services/gcal/postGCalsToSync";
import { AmplitudePageName, track } from "utils/amplitude";
import { ReactComponent as GoogleCalendarIcon } from "assets/svg/gcal-icon.svg";
import { ReactComponent as CalendarSynced } from "assets/svg/calendar-synced.svg";
import Loading from "components/Loading";
import { useNavigate } from "react-router-dom";
import styles from "./GoogleCalendar.module.scss";

const ANALYTICS_COMPONENT_NAME = "select-google-cal-modal";

/**
 * renders a modal to select calendars to sync
 */
const SelectGCalsModal = ({
  showSelectCalendarsModal,
  setShowSelectCalendarsModal,
  analyticsPageName,
  initialSyncedCalendars,
  setGCals,
  setVisibleCalendarIds,
}: {
  showSelectCalendarsModal: boolean;
  setShowSelectCalendarsModal: Dispatch<SetStateAction<boolean>>;
  analyticsPageName: AmplitudePageName;
  initialSyncedCalendars: CalendarEntry[];
  setGCals: Dispatch<SetStateAction<CalendarEntry[]>>;
  setVisibleCalendarIds: Dispatch<SetStateAction<string[]>>;
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<any>();
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [calendars, setCalendars] = useState<CalendarEntry[]>([]);
  const [selected, setSelected] = useState<CalendarEntry[]>(
    initialSyncedCalendars ?? [],
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    setIsLoadingCalendars(true);
    getAllGCals()
      .then(setCalendars)
      .catch(console.error)
      .finally(() => setIsLoadingCalendars(false));

    // getSelectedGCals().then(setSelected).catch(console.error);
  }, []);

  const save = async () => {
    try {
      setIsSaving(true);

      const selectedToSync = selected.map((el) => ({
        calendar_id: el.calendar_id,
        calendar_name: el.summary,
      }));

      const res = await postGCalsToSync(selectedToSync);
      setGCals(res);
      setVisibleCalendarIds(
        res
          .filter((calendar) => !calendar.hidden)
          .map((calendar) => calendar.calendar_id),
      );

      if (
        window.location.pathname.includes(
          "/pro-dashboard/settings/integrations",
        )
      ) {
        setShowSuccessModal(true);
      } else {
        setShowSelectCalendarsModal(false);
      }
    } catch (error) {
      setError(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (calendars.length === 0) {
    return null;
  }

  if (showSuccessModal) {
    return (
      <ModalLayout
        size={"md"}
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        showHeaderDivider={false}
        dialogClassName={styles["calendar-synced__dialog"]}
        headerClass={styles["calendar-synced__header"]}
        bodyClass={styles["calendar-synced__body"]}
        footerClass={styles["calendar-synced__footer"]}
      >
        <CalendarSynced />
        <div className={styles["calendar-synced__title"]}>Calendar synced</div>
        <p className={styles["calendar-synced__text"]}>
          Your Google Calendar events, out-of-office status, and focus time will
          be reflected in your Orchid Calendar.
        </p>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate("/pro-dashboard/schedule");
          }}
          className={styles["calendar-synced__btn"]}
        >
          Go to Calendar
        </Button>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout
      error={error}
      showHeaderDivider={true}
      show={showSelectCalendarsModal && !showSuccessModal}
      onHide={() => {
        track(
          analyticsPageName,
          [ANALYTICS_COMPONENT_NAME, "close-modal-button"],
          "click",
        );
        setShowSelectCalendarsModal(false);
      }}
      backdrop="static"
      keyboard={false}
      Header={
        <>
          <GoogleCalendarIcon />
          Connect Google Calendar
        </>
      }
      contentClassName={styles["select-modal__content"]}
      headerClass={styles["select-modal__header"]}
      bodyClass={styles["select-modal__body"]}
      Footer={
        <>
          <Button
            variant="outline-primary"
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                "click",
              );
              setShowSelectCalendarsModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={isSaving || selected.length === 0}
            size="sm"
            className={styles["select-modal__button"]}
            onClick={() => save()}
          >
            {isSaving ? "Saving..." : messages.save}
          </Button>
        </>
      }
      footerClass={styles["select-modal__footer"]}
    >
      <div>
        Your Google Calendar events, out-of-office status, and focus time will
        be reflected in your Orchid Calendar.
      </div>

      <ul className={"mb-0"}>
        {/* <li>Your availability on Orchid will be blocked accordingly. </li> */}
        <li>To edit Google events, please use Google Calendar.</li>
        <li>Event details will be visible only to you.</li>
      </ul>
      <div>
        <hr className={"m-0"} />
      </div>

      <div>Check all Calendars you want to sync from Google Calendar.</div>
      <div>
        {isLoadingCalendars ? (
          <Loading className={"my-0"} />
        ) : (
          calendars.map((el) => (
            <div
              key={el.calendar_id}
              className={styles["select-modal__checkbox"]}
            >
              <CustomSelectionControl
                type="checkbox"
                checked={selected.some((c) => c.calendar_id === el.calendar_id)}
                onChange={() => {
                  if (selected.some((c) => c.calendar_id === el.calendar_id)) {
                    setSelected(
                      selected.filter((c) => c.calendar_id !== el.calendar_id),
                    );
                  } else {
                    setSelected([...selected, el]);
                  }
                }}
              />
              {el.summary || el.calendar_id}
            </div>
          ))
        )}
      </div>
    </ModalLayout>
  );
};

export default SelectGCalsModal;
